import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useCreateWhaticketOfficial, useUpdateWhaticketOfficial } from '@/hooks/useChannelConnections';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-handler';
import type { CreateWhaticketOfficialChannelDto, UpdateWhaticketOfficialChannelDto } from '@/types/channels';

// Masked value constant
const MASKED_VALUE = '********';

// Schema for create mode - all fields required
const createSchema = z.object({
  name: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
  apiUrl: z.string().url('URL inválida').min(1, 'URL da API é obrigatória'),
  apiKey: z.string().min(1, 'API Key é obrigatória'),
});

// Schema for edit mode - name and apiUrl required, apiKey optional (can be masked)
const editSchema = z.object({
  name: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
  apiUrl: z.string().url('URL inválida').min(1, 'URL da API é obrigatória'),
  apiKey: z.string().optional(),
});

type WhaticketOfficialFormData = z.infer<typeof createSchema>;

interface WhaticketOfficialFormProps {
  mode?: 'create' | 'edit';
  channelId?: string;
  initialValues?: Partial<WhaticketOfficialFormData>;
  onSuccess: () => void;
  onCancel: () => void;
}

export function WhaticketOfficialForm({
  mode = 'create',
  channelId,
  initialValues,
  onSuccess,
  onCancel
}: WhaticketOfficialFormProps) {
  const { mutate: createChannel, isLoading: isCreating, error: createError } = useCreateWhaticketOfficial();
  const { mutate: updateChannel, isLoading: isUpdating, error: updateError } = useUpdateWhaticketOfficial(channelId || '');
  const { success, error: showError } = useToast();

  // Track if API key was changed in edit mode
  const [apiKeyChanged, setApiKeyChanged] = useState(false);

  const isLoading = mode === 'create' ? isCreating : isUpdating;
  const error = mode === 'create' ? createError : updateError;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<WhaticketOfficialFormData>({
    resolver: zodResolver(mode === 'create' ? createSchema : editSchema),
    defaultValues: mode === 'edit'
      ? {
          name: initialValues?.name || '',
          apiUrl: initialValues?.apiUrl || '',
          apiKey: '', // Empty in edit mode - will show placeholder
        }
      : {
          name: '',
          apiUrl: '',
          apiKey: '',
        },
  });

  const onSubmit = (data: WhaticketOfficialFormData) => {
    if (mode === 'create') {
      const dto: CreateWhaticketOfficialChannelDto = {
        name: data.name,
        apiUrl: data.apiUrl,
        apiKey: data.apiKey,
      };

      createChannel(dto, {
        onSuccess: () => {
          success('Canal criado com sucesso!', { id: 'channel-create' });
          onSuccess();
        },
        onError: (err) => {
          const message = getErrorMessage(err, 'Erro ao criar canal');
          showError(message, { id: 'channel-create-error' });
        },
      });
    } else {
      // In edit mode
      const dto: UpdateWhaticketOfficialChannelDto = {
        name: data.name,
        apiUrl: data.apiUrl,
      };

      // Handle API key - send masked value if not changed, actual value if changed
      if (apiKeyChanged && data.apiKey && data.apiKey.trim()) {
        dto.apiKey = data.apiKey; // User provided new key
      } else {
        dto.apiKey = MASKED_VALUE; // Keep existing key
      }

      updateChannel(dto, {
        onSuccess: () => {
          success('Canal atualizado com sucesso!', { id: `channel-update-${channelId}` });
          onSuccess();
        },
        onError: (err) => {
          const message = getErrorMessage(err, 'Erro ao atualizar canal');
          showError(message, { id: `channel-update-error-${channelId}` });
        },
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            {error instanceof Error ? error.message : mode === 'create' ? 'Erro ao criar canal' : 'Erro ao atualizar canal'}
          </AlertDescription>
        </Alert>
      )}

      {/* Name Field */}
      <div className="space-y-2">
        <Label htmlFor="name">Nome do Canal</Label>
        <Input
          id="name"
          placeholder="Ex: Suporte WhatsApp"
          {...register('name')}
          disabled={isLoading}
        />
        {errors.name && (
          <p className="text-sm text-destructive">{errors.name.message}</p>
        )}
      </div>

      {/* API URL Field */}
      <div className="space-y-2">
        <Label htmlFor="apiUrl">
          URL da API Whaticket
          <span className="text-muted-foreground text-xs ml-2">
            (ex: https://whaticket.example.com/api)
          </span>
        </Label>
        <Input
          id="apiUrl"
          type="url"
          placeholder="https://whaticket.example.com/api"
          autoComplete="off"
          {...register('apiUrl')}
          disabled={isLoading}
        />
        {errors.apiUrl && (
          <p className="text-sm text-destructive">{errors.apiUrl.message}</p>
        )}
      </div>

      {/* API Key Field */}
      <div className="space-y-2">
        <Label htmlFor="apiKey">
          API Key
          <span className="text-muted-foreground text-xs ml-2">
            {mode === 'create' ? '(chave de autenticação do Whaticket)' : '(deixe em branco para manter a chave atual)'}
          </span>
        </Label>
        <Input
          id="apiKey"
          type="password"
          placeholder={mode === 'create' ? '••••••••••••••••' : 'Deixe em branco para não alterar'}
          autoComplete="new-password"
          {...register('apiKey')}
          onChange={(e) => {
            if (mode === 'edit' && e.target.value) {
              setApiKeyChanged(true);
            }
          }}
          disabled={isLoading}
        />
        {errors.apiKey && (
          <p className="text-sm text-destructive">{errors.apiKey.message}</p>
        )}
        {mode === 'edit' && (
          <p className="text-xs text-muted-foreground">
            A API Key atual está protegida. Digite uma nova chave apenas se desejar alterá-la.
          </p>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <Button type="submit" disabled={isLoading} className="flex-1">
          {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {mode === 'create' ? 'Criar Canal' : 'Atualizar Canal'}
        </Button>
      </div>
    </form>
  );
}
